import React, { useCallback } from 'react';
import { caseStatusMap } from '@web/utils';
import { CidCvnCheckResult, CidCvnRecord } from '@packages/models/api/cid-cvn';
import { CidCvnCheckResultBadge } from '../cid-cvn-check-result-badge';
import { CidCvnTableActions } from './cid-cvn-table-actions';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { getDisplayDate, getDisplayValue } from '@web/utils';
import { Icon, Typography } from '@packages/ui/shared';
import { Link, useLocation } from 'react-router-dom';
import { TableCell, TableRow } from '@web/components/table';
import { TechlineStatusCode } from '@packages/models/api';

const useStyles = createUseStyles({
    actionsCell: {
        display: 'flex',
        justifyContent: 'start',
        padding: '10px',
    },
    progress: {
        width: '90%',
    },
    reportName: {
        fontSize: 15,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline !important',
        },
    },
    resultStatusCell: {
        alignItems: 'center',
        display: 'flex',
        height: '60px',
        justifyContent: 'left',
    },
});

interface ReportNameTableCellProps {
    id: string;
    reportName: string;
    result: CidCvnCheckResult;
    showAsText: boolean;
    onReportNameClick: (id: string, reportName: string, result: CidCvnCheckResult) => void;
}

function ReportNameTableCell({ id, reportName, result, showAsText, onReportNameClick }: ReportNameTableCellProps) {
    const { pathname } = useLocation();
    const classes = useStyles();
    const path = `${pathname}/${id}`;

    let content;

    if (showAsText) {
        content = <Typography numberOfLines={1}>{reportName}</Typography>;
    } else if (!result) {
        content = (
            <Link
                to="#"
                className={classes.reportName}
                onClick={(e) => e.preventDefault()}
                style={{ opacity: 0.5, pointerEvents: 'none' }}
            >
                {reportName}
            </Link>
        );
    } else {
        content = (
            <Link to={path} className={classes.reportName} onClick={() => onReportNameClick(id, reportName, result)}>
                {reportName}
            </Link>
        );
    }
    return <TableCell>{content}</TableCell>;
}

interface CheckResultTableCellProps {
    result: CidCvnCheckResult;
}

function CheckResultTableCell({ result }: CheckResultTableCellProps) {
    const classes = useStyles();

    let content;

    if (!result) {
        content = <Icon name="x-close" />;
    } else {
        content = <CidCvnCheckResultBadge result={result} />;
    }

    return <TableCell className={classes.resultStatusCell}>{content}</TableCell>;
}

interface CidCvnTableBodyProps {
    canPerformCidCvnCheck: boolean;
    canViewDetailedResults: boolean;
    canViewTechlineCase: boolean;
    canViewTechlineDraftCase: boolean;
    data: CidCvnRecord;
    isDownloading: boolean;
    isRetrying: boolean;
    onDownload: (id: string) => Promise<void>;
    onRetry: (id: string) => void;
    onReportNameClick: (id: string, reportName: string, result: CidCvnCheckResult) => void;
    onViewCase: (caseNumber: string, result: CidCvnCheckResult) => void;
}

export function CidCvnTableBody({
    canPerformCidCvnCheck,
    canViewDetailedResults,
    canViewTechlineCase,
    canViewTechlineDraftCase,
    data,
    isDownloading,
    isRetrying,
    onDownload,
    onRetry,
    onReportNameClick,
    onViewCase,
}: CidCvnTableBodyProps) {
    const classes = useStyles();
    const createdAccountName = data?.canViewDiagnosticInfo ? data?.createdAccountName : 'Subaru Technician';

    const id = data?.cidCvnStatusId;
    const caseNumber = data?.canViewDiagnosticInfo ? data?.techlineCaseNumber : undefined;
    const techlineCaseStatus = data?.canViewDiagnosticInfo ? data?.techlineCaseStatus : undefined;
    const isCaseClosed = caseStatusMap[TechlineStatusCode.CLOSED].includes(techlineCaseStatus);
    const isResultNoMatchFound = data.result === CidCvnCheckResult.NoMatchFound;

    const showViewCaseButton = !!caseNumber;
    const canViewCase =
        techlineCaseStatus === TechlineStatusCode.DRAFT
            ? canViewTechlineCase && canViewTechlineDraftCase
            : canViewTechlineCase;

    const canRetryCheck = !!caseNumber && isResultNoMatchFound && isCaseClosed && canPerformCidCvnCheck;

    const handleViewCaseButtonClick = useCallback(() => {
        if (caseNumber) {
            onViewCase(caseNumber, data.result);
        }
    }, [onViewCase, caseNumber, data.result]);

    const handleRetryButtonClick = useCallback(() => {
        onRetry(id);
    }, [onRetry, id]);

    const handleDownloadButtonClick = useCallback(() => {
        onDownload(id);
    }, [onDownload, id]);

    return (
        <>
            <TableRow key={data.cidCvnStatusId}>
                <ReportNameTableCell
                    id={data.cidCvnStatusId}
                    reportName={data.reportName}
                    result={data.result}
                    showAsText={!canViewDetailedResults}
                    onReportNameClick={onReportNameClick}
                />
                <CheckResultTableCell result={data.result} />
                <TableCell>
                    <Typography numberOfLines={1}>{getDisplayValue(createdAccountName)}</Typography>
                </TableCell>
                <TableCell>
                    <Typography numberOfLines={1}>
                        {getDisplayDate(data.createdTimestamp, DATE_FORMAT.MMM_D_YEAR)}
                    </Typography>
                </TableCell>
                <TableCell className={classes.actionsCell}>
                    <CidCvnTableActions
                        showViewCaseButton={showViewCaseButton}
                        isViewCaseButtonDisabled={!canViewCase}
                        onViewCase={handleViewCaseButtonClick}
                        showRetryButton={isResultNoMatchFound}
                        isRetryButtonDisabled={!canRetryCheck || isRetrying}
                        onRetry={handleRetryButtonClick}
                        isRetrying={isRetrying}
                        isDownloading={isDownloading}
                        isDownloadButtonDisabled={!canViewDetailedResults || isDownloading}
                        onDownload={handleDownloadButtonClick}
                    />
                </TableCell>
            </TableRow>
        </>
    );
}
